import type { StateCreator } from 'zustand';
import { areObjectIdsEqual } from 'helpers/idComparison';
import { DocumentsOnInspectionView } from 'presentation/document/documentsToDocumentsOnInspectionView';
import { DocumentsOnTemplateView } from 'presentation/document/documentsOnTemplateView';
import { showChannelRequestErrorToast } from 'services/toaster';
import { inspectionClient } from 'shared/domain/inspection/inspectionClient';
import type { InspectionModel } from 'shared/domain/inspection/inspectionModel';
import { collectProtocolItemIdsInSectionTree } from 'shared/domain/inspection/mapping/collectProtocolItemIdsInSectionTree';
import { mergeProtocolAnswerIntoInspectionModel } from 'shared/domain/inspection/mapping/mergeProtocolAnswerIntoInspectionModel';
import { mergeProtocolIssuesOnInspectionModel } from 'shared/domain/inspection/mapping/mergeProtocolIssuesOnInspectionModel';
import { mergeSectionApplicabilityIntoProtocol } from 'shared/domain/inspection/mapping/mergeSectionApplicabilityIntoProtocol';
import { isDataAndTableUnchanged } from 'shared/domain/messages/message';
import type { InspectionStoreState } from '../../store';
import { isSkippedDuringAtomicSave } from '../../utils/isSkippedDuringAtomicSave';
import type { InspectionSlice } from './types';
import { createInspectionSaveActions } from './save';

/** Changes often come in bursts (a save sends many): the open inspection is read once after them. */
const RELOAD_AFTER_CHANGES_DELAY_MS = 500;

const EMPTY_INSPECTION_DOCUMENTS: DocumentsOnInspectionView = {};
const EMPTY_TEMPLATE_DOCUMENTS: DocumentsOnTemplateView = {
  templateDocuments: [],
  checklistDocuments: {},
  sectionDocuments: {},
};

const getDefaultState = () => ({
  inspectionId: undefined,
  inspectionModel: undefined,
  inspection: undefined,
  inspectionLoading: true,
  inspectionDocuments: EMPTY_INSPECTION_DOCUMENTS,
  inspectionTemplateDocuments: EMPTY_TEMPLATE_DOCUMENTS,
  inspectionDocumentsLoading: true,
  ignoreDocumentsStore: true,
  atomicSaveEnabled: false,
});

type InspectionSliceCreator = StateCreator<
  InspectionStoreState,
  [],
  [],
  InspectionSlice
>;

/**
 * The open inspection and the filling of its protocol, through
 * `inspectionClient`. Reads are numbered: only the latest one lands in the
 * store. A change superseded by a newer one of the same field (its `signal`
 * is aborted) is still saved, but its answer no longer touches the store.
 */
export const createInspectionSlice: InspectionSliceCreator = (set, get, store) => {
  /** Not state: bumped by every read and reset, so older answers are dropped. */
  let latestRead = 0;

  /** Keeps the protocol issue ids known locally when the same inspection is read again. */
  const withLocalProtocolIssues = (read: InspectionModel): InspectionModel => {
    const shown = get().inspectionModel;
    return shown && shown._id === read._id ? mergeProtocolIssuesOnInspectionModel(shown, read) : read;
  };

  const isOpen = (inspectionId: string): boolean => {
    const { inspectionModel } = get();
    return Boolean(inspectionModel && areObjectIdsEqual(inspectionModel._id, inspectionId));
  };

  return {
    ...getDefaultState(),

    setInspectionId: (inspectionId) =>
      set((state) => ({
        inspectionId,
        inspectionLoading: inspectionId ? state.inspectionLoading : false,
      })),

    beginInspectionNavigation: (inspectionId) =>
      set({
        inspectionId,
        inspection: undefined,
        inspectionModel: undefined,
        inspectionLoading: true,
        inspectionDocuments: EMPTY_INSPECTION_DOCUMENTS,
        inspectionTemplateDocuments: EMPTY_TEMPLATE_DOCUMENTS,
        inspectionDocumentsLoading: true,
        ignoreDocumentsStore: true,
      }),

    openInspection: async (inspectionId) => {
      const read = ++latestRead;
      const isLatest = (): boolean => read === latestRead;
      const { inspectionModel, inspectionId: shownId } = get();
      if (inspectionModel?._id !== inspectionId || shownId !== inspectionId) {
        set({ inspectionLoading: true });
      }
      try {
        const inspection = await inspectionClient.getOneById(inspectionId);
        if (isLatest()) {
          set({ inspectionId: inspection._id || inspectionId, inspectionModel: withLocalProtocolIssues(inspection) });
        }
      } catch (error) {
        if (isLatest()) {
          showChannelRequestErrorToast('Error while getting inspection', error);
        }
      } finally {
        if (isLatest()) {
          set({ inspectionLoading: false });
        }
      }
    },

    reloadInspection: async () => {
      const { inspectionId } = get();
      if (inspectionId) {
        await get().openInspection(inspectionId);
      }
    },

    subscribeToInspectionChanges: () => {
      let pendingReload: ReturnType<typeof setTimeout> | undefined;
      const stopEvents = inspectionClient.onEvent((event) => {
        const { isSaving, atomicSaveEnabled } = get();
        if (isDataAndTableUnchanged(event) || isSaving || isSkippedDuringAtomicSave(event, atomicSaveEnabled)) {
          return;
        }
        clearTimeout(pendingReload);
        pendingReload = setTimeout(() => void get().reloadInspection(), RELOAD_AFTER_CHANGES_DELAY_MS);
      });
      return () => {
        stopEvents();
        clearTimeout(pendingReload);
      };
    },

    setInspectionModel: (inspectionModel) => set({ inspectionModel }),

    appendProtocolItemIssue: (protocolItemId, issueId) => {
      const model = get().inspectionModel;
      if (!model) {
        return;
      }

      set({
        inspectionModel: {
          ...model,
          protocol: model.protocol.map((item) => {
            if (!areObjectIdsEqual(item._id, protocolItemId)) {
              return item;
            }

            const existingIssues = item.complianceCheck.issues ?? [];
            if (
              existingIssues.some((id) => areObjectIdsEqual(id, issueId))
            ) {
              return item;
            }

            return {
              ...item,
              complianceCheck: {
                ...item.complianceCheck,
                issues: [...existingIssues, issueId],
              },
            };
          }),
        },
      });
    },

    setInspection: (inspection) => set({ inspection }),
    setInspectionLoading: (inspectionLoading) => set({ inspectionLoading }),
    setInspectionDocuments: (inspectionDocuments) => set({ inspectionDocuments }),
    setInspectionTemplateDocuments: (inspectionTemplateDocuments) =>
      set({ inspectionTemplateDocuments }),
    setInspectionDocumentsLoading: (inspectionDocumentsLoading) =>
      set({ inspectionDocumentsLoading }),
    setIgnoreDocumentsStore: (ignoreDocumentsStore) =>
      set({ ignoreDocumentsStore }),

    postInspection: (templateId, inspectionOutDto, projectId) =>
      inspectionClient.create({ templateId, inspectionOutDto, projectId }),

    putInspection: (inspectionId, inspectionOutDto, _signal, projectId) =>
      inspectionClient.edit({ inspectionId, inspectionOutDto, projectId }),

    putProtocol: (inspectionId, protocolItemId, protocolItemOutDto, _signal, projectId) =>
      inspectionClient.editProtocolItem({ inspectionId, protocolItemId, protocolItemOutDto, projectId }),

    putProtocolAnswer: async (inspectionId, protocolItemId, protocolAnswerId, protocolAnswerOutDto, signal, projectId) => {
      const answer = await inspectionClient.editProtocolAnswer({
        inspectionId,
        protocolItemId,
        protocolAnswerId,
        protocolAnswerOutDto,
        projectId,
      });
      const { inspectionModel } = get();
      if (!signal.aborted && inspectionModel && isOpen(inspectionId)) {
        set({ inspectionModel: mergeProtocolAnswerIntoInspectionModel(inspectionModel, protocolItemId, answer) });
      }
      return answer;
    },

    putProtocolSection: (inspectionId, sectionId, protocolSectionOutDto, projectId) =>
      inspectionClient.editProtocolSection({ inspectionId, sectionId, protocolSectionOutDto, projectId }),

    putProtocolSectionAnswersBatch: async (inspectionId, sectionId, body, signal, projectId) => {
      const { inspection, inspectionModel } = get();
      const protocolItemIds = collectProtocolItemIdsInSectionTree({
        protocolSectionId: sectionId,
        protocolSections: inspection?.sections ?? inspectionModel?.sections ?? [],
        protocol: inspection?.protocol ?? inspectionModel?.protocol ?? [],
        checklistItems: inspection?.template?.checklist,
        checklistSections: inspection?.template?.sections,
      });
      const hasItems = protocolItemIds.length > 0;

      const applySectionApplicability = (): void => {
        const current = get();
        if (!current.inspectionModel) {
          return;
        }
        const checklistItems = current.inspection?.template?.checklist;
        set({
          inspectionModel: {
            ...current.inspectionModel,
            protocol: mergeSectionApplicabilityIntoProtocol(
              current.inspectionModel.protocol,
              protocolItemIds,
              body.isApplicable,
              checklistItems
            ),
          },
          inspection: current.inspection && {
            ...current.inspection,
            protocol: mergeSectionApplicabilityIntoProtocol(
              current.inspection.protocol,
              protocolItemIds,
              body.isApplicable,
              checklistItems
            ),
          },
        });
      };

      if (hasItems) {
        applySectionApplicability();
      }

      try {
        const result = await inspectionClient.editProtocolSectionAnswers({
          inspectionId,
          sectionId,
          protocolSectionAnswersBatchOutDto: body,
          protocolItemIds,
          projectId,
        });
        if (signal.aborted || !isOpen(inspectionId)) {
          return result;
        }
        if (hasItems) {
          applySectionApplicability();
        } else {
          await get().openInspection(inspectionId);
        }
        return result;
      } catch (error) {
        // A superseded change leaves the store to the newer one.
        if (hasItems && !signal.aborted) {
          set({ inspectionModel, inspection });
        }
        throw error;
      }
    },

    ...createInspectionSaveActions(set, get, store),
    
    setAtomicSaveEnabled: (atomicSaveEnabled) => {
      const wasEnabled = get().atomicSaveEnabled;
      set({ atomicSaveEnabled });
      if (wasEnabled && !atomicSaveEnabled) {
        // The list skipped the changes saved meanwhile (`isSkippedDuringAtomicSave`).
        get().inspectionsCollection.requestReload();
      }
    },


    resetInspection: () => {
      latestRead += 1;
      set(getDefaultState());
    },
  };
};

export type {
  InspectionSlice,
  InspectionSubmitSlice,
  InspectionSubmitResponse,
  SetableInspectionFields,
  OnDone,
  SubmitForm,
} from './types';
